// Crops and shrinks kitchen menu photos in the browser before upload, so
// oversized phone photos never hit the server's upload limit.

/** Longest side of an uploaded menu photo — plenty for a full-width card on a retina phone. */
const MAX_DIMENSION = 1600;
/** Keep well under the server's upload limit (PHP's default is 2 MB). */
const TARGET_BYTES = 900 * 1024;

/** Loads a picked file as an image, rejecting formats the browser can't decode (e.g. HEIC outside Safari). */
export const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("unreadable-image"));
    image.src = src;
  });

const toBlob = (canvas, quality) =>
  new Promise((resolve, reject) =>
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("encode-failed"))), "image/jpeg", quality)
  );

/**
 * Crops the image to `crop` (react-easy-crop's `croppedAreaPixels`), scales it
 * down to MAX_DIMENSION and re-encodes it as a JPEG small enough to upload,
 * stepping the quality down if needed. A 12 MP phone photo typically comes
 * out around 200–400 KB.
 */
export async function cropAndCompress(src, crop, fileName) {
  const image = await loadImage(src);
  const scale = Math.min(1, MAX_DIMENSION / Math.max(crop.width, crop.height));

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(crop.width * scale);
  canvas.height = Math.round(crop.height * scale);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("encode-failed");

  // JPEG has no transparency — give PNG cut-outs a white background, not black.
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.imageSmoothingQuality = "high";
  context.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height);

  let blob = await toBlob(canvas, 0.85);
  for (const quality of [0.75, 0.65, 0.55]) {
    if (blob.size <= TARGET_BYTES) break;
    blob = await toBlob(canvas, quality);
  }

  const baseName = fileName.replace(/\.[^.]+$/, "") || "photo";
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
}

export const formatFileSize = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
